import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../../context/MyContext';
import { Tooltip, Modal, Table, Button, message, Form, Input, Switch, InputNumber } from 'antd';
import FilterIcon from '../../icons/FilterIcon';
import { api, API_BASE_URL } from "../../config/api";
import MultiCountrySelectInput from '../shared/countrySelectInput/MultiCountrySelectInput';
// Import CountriesJSON
import { CountriesJSON } from '../shared/countriesJSON';

// CHANGED: Added iorSearchQuery to props
const UpdateIOR = ({ unloadCountryQuery, setUnloadCountryQuery, activeToggle, setActiveToggle, iorSearchQuery }) => {
  const { user } = useMyContext();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [dropdown, setDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Disable Country States
  const [disabledCountriesData, setDisabledCountriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountriesToDisable, setSelectedCountriesToDisable] = useState([]);
  const [showConfirmDisableModal, setShowConfirmDisableModal] = useState(false);

  // Disable Duty & VAT States
  const [disabledDutyVatData, setDisabledDutyVatData] = useState([]);
  const [selectedCountriesToDisableDutyVat, setSelectedCountriesToDisableDutyVat] = useState([]);
  const [showConfirmDisableDutyVatModal, setShowConfirmDisableDutyVatModal] = useState(false);

  // ECCN States
  const [eccnData, setEccnData] = useState([]);
  const [showAddEccnModal, setShowAddEccnModal] = useState(false);
  const [showEditEccnModal, setShowEditEccnModal] = useState(false);
  const [showDeleteEccnModal, setShowDeleteEccnModal] = useState(false);
  const [currentEccn, setCurrentEccn] = useState(null);
  const [eccnForm] = Form.useForm();
  const [editEccnForm] = Form.useForm();

  const [disabledHsCodeData, setDisabledHsCodeData] = useState([]);
  const [hsCodeToDisable, setHsCodeToDisable] = useState('');
  const [showConfirmDisableHsCodeModal, setShowConfirmDisableHsCodeModal] = useState(false);

  // --- NEW: LEAD TIMES STATES ---
  const [leadTimesData, setLeadTimesData] = useState([]);
  const [showLeadTimeModal, setShowLeadTimeModal] = useState(false);
  const [currentLeadTimeCountry, setCurrentLeadTimeCountry] = useState(null);
  const [leadTimeForm] = Form.useForm();

  // Toggle options for the center section
  const toggleOptions = [
    { key: "estimation", label: "Estimation Services" },
    { key: "disableCountry", label: "Disable Country" },
    { key: "eccn", label: "ECCN" },
    { key: "disableDutyVat", label: "Disable Duty & VAT" },
    { key: "disableHsCode", label: "Disable HS Code" },
    // Added Lead Times tab
    { key: "leadTimes", label: "Lead Times" },
  ];

  // --- PAGINATION CONFIGURATION ---
  const paginationConfig = {
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  };

  // --- CHANGED: FILTERING LOGIC FOR SEARCH BAR ---
  // Helper function to filter data based on iorSearchQuery
  const getFilteredData = (data, keysToCheck) => {
    if (!iorSearchQuery) return data;
    const query = iorSearchQuery.toLowerCase();
    return data.filter(item =>
      keysToCheck.some(key =>
        item[key] && item[key].toString().toLowerCase().includes(query)
      )
    );
  };

  // Memoize filtered data for each table to avoid unnecessary recalculations
  const filteredDisabledCountries = useMemo(() =>
    getFilteredData(disabledCountriesData, ['countryName', 'disabledBy']),
    [disabledCountriesData, iorSearchQuery]);

  const filteredEccnData = useMemo(() =>
    getFilteredData(eccnData, ['eccn_code', 'description']),
    [eccnData, iorSearchQuery]);

  const filteredDutyVatData = useMemo(() =>
    getFilteredData(disabledDutyVatData, ['countryName', 'disabledBy']),
    [disabledDutyVatData, iorSearchQuery]);

  const filteredHsCodeData = useMemo(() =>
    getFilteredData(disabledHsCodeData, ['hsCode', 'disabledBy']),
    [disabledHsCodeData, iorSearchQuery]);

  const filteredLeadTimesData = useMemo(() =>
    getFilteredData(leadTimesData, ['countryName']),
    [leadTimesData, iorSearchQuery]);


  useEffect(() => {
    const fetchUnloadCountries = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/estimates/unload-countries`);
        const uniqueCountries = response.data;
        setAllCountries(uniqueCountries.map(country => ({ value: country })));
        setFilteredOptions(uniqueCountries.map(country => ({ value: country })));

      } catch (error) {
        console.error("Error fetching unload countries:", error);
        message.error("Failed to load country filter options.");
      }
    };

    fetchUnloadCountries();

    if (activeToggle === "disableCountry") {
      fetchDisabledCountries();
    } else if (activeToggle === "eccn") {
      fetchEccnCodes();
    } else if (activeToggle === "disableDutyVat") {
      fetchDisabledDutyVatCountries();
    } else if (activeToggle === "disableHsCode") {
      fetchDisabledHsCodes();
    } else if (activeToggle === "leadTimes") {
      fetchLeadTimes();
    }
  }, [activeToggle]);

  // Fetch disabled countries list
  const fetchDisabledCountries = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/countries/disabled`);
      setDisabledCountriesData(response.data);
    } catch (error) {
      console.error("Error fetching disabled countries:", error);
      message.error("Failed to fetch disabled countries");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch disabled duty & vat countries list
  const fetchDisabledDutyVatCountries = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/duty-vat/disabled`);
      setDisabledDutyVatData(response.data);
    } catch (error) {
      console.error("Error fetching disabled duty & VAT countries:", error);
      message.error("Failed to fetch disabled duty & VAT countries");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDisabledHsCodes = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/hs-settings/disabled`);
      setDisabledHsCodeData(response.data);
    } catch (error) {
      console.error("Error fetching disabled HS codes:", error);
      message.error("Failed to fetch disabled HS codes");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch ECCN codes
  const fetchEccnCodes = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/eccn`);
      setEccnData(response.data);
    } catch (error) {
      console.error("Error fetching ECCN codes:", error);
      message.error("Failed to fetch ECCN codes");
    } finally {
      setIsLoading(false);
    }
  };

  // --- NEW: FETCH LEAD TIMES ---
  const fetchLeadTimes = async () => {
    setIsLoading(true);
    try {
      // Get Saved Lead Times from DB
      const response = await api.get(`${API_BASE_URL}/lead-times`);
      const savedLeadTimes = response.data;

      // Merge with static CountriesJSON
      // Map CountriesJSON to table format, checking if DB entry exists
      const formattedData = CountriesJSON.map(c => {
        const saved = savedLeadTimes.find(l => l.countryName === c.Country);
        return {
          countryName: c.Country,
          fromDays: saved ? saved.fromDays : null,
          toDays: saved ? saved.toDays : null,
          isSaved: !!saved
        };
      });

      setLeadTimesData(formattedData);
    } catch (error) {
      console.error("Error fetching lead times:", error);
      message.error("Failed to fetch lead times");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (dropdown && searchInputRef.current) {
      searchInputRef.current.value = searchQuery;
    }
  }, [dropdown, searchQuery]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim() === '') {
      setFilteredOptions(allCountries);
    } else {
      const filteredCountries = allCountries.filter(country =>
        country.value.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filteredCountries);
    }
    setDropdown(true);
  };

  const handleCountrySelect = (country) => {
    const isSelected = selectedCountries.includes(country.value);
    const newSelectedCountries = isSelected
      ? selectedCountries.filter(c => c !== country.value)
      : [...selectedCountries, country.value];

    setSelectedCountries(newSelectedCountries);
    setUnloadCountryQuery(newSelectedCountries);
    setSelectAll(newSelectedCountries.length === allCountries.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCountries([]);
      setUnloadCountryQuery([]);
      setSelectAll(false);
    } else {
      const allValues = allCountries.map(country => country.value);
      setSelectedCountries(allValues);
      setUnloadCountryQuery(allValues);
      setSelectAll(true);
    }
  };

  const handleDropdownToggle = () => {
    if (!dropdown) {
      if (searchQuery.trim() === '') {
        setFilteredOptions(allCountries);
      } else {
        const filteredCountries = allCountries.filter(country =>
          country.value.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredOptions(filteredCountries);
      }
    }
    setDropdown(!dropdown);
  };

  const handleToggleClick = (key) => {
    setActiveToggle(key);
  };

  // Handle country selection for disabling
  const handleCountrySelectionForDisable = (e) => {
    setSelectedCountriesToDisable(e.target.value);
  };

  // Handle country selection for disabling duty & vat
  const handleCountrySelectionForDisableDutyVat = (e) => {
    setSelectedCountriesToDisableDutyVat(e.target.value);
  };

  const handleHsCodeChange = (e) => {
    setHsCodeToDisable(e.target.value.trim());
  };

  // Handle disabling countries
  const handleDisableCountries = async () => {
    setShowConfirmDisableModal(false);
    setIsLoading(true);

    try {
      await api.post(`${API_BASE_URL}/countries/disable`, {
        countries: selectedCountriesToDisable,
        disabledBy: user?.name || user?.email || 'System'
      });

      message.success("Countries disabled successfully");
      fetchDisabledCountries();
      setSelectedCountriesToDisable([]);
    } catch (error) {
      console.error("Error disabling countries:", error);
      message.error("Failed to disable countries");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle disabling duty & vat for countries
  const handleDisableDutyVatCountries = async () => {
    setShowConfirmDisableDutyVatModal(false);
    setIsLoading(true);

    try {
      await api.post(`${API_BASE_URL}/duty-vat/disable`, {
        countries: selectedCountriesToDisableDutyVat,
        disabledBy: user?.name || user?.email || 'System'
      });

      message.success("Duty & VAT disabled for countries successfully");
      fetchDisabledDutyVatCountries();
      setSelectedCountriesToDisableDutyVat([]);
    } catch (error) {
      console.error("Error disabling duty & VAT for countries:", error);
      message.error("Failed to disable duty & VAT for countries");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableHsCode = async () => {
    setShowConfirmDisableHsCodeModal(false);
    setIsLoading(true);

    try {
      await api.post(`${API_BASE_URL}/hs-settings/disable`, {
        hscode: hsCodeToDisable,
        disabledBy: user?.name || user?.email || 'System'
      });

      message.success(`HS Code ${hsCodeToDisable} disabled successfully`);
      fetchDisabledHsCodes();
      setHsCodeToDisable('');
    } catch (error) {
      console.error("Error disabling HS Code:", error);
      message.error("Failed to disable HS Code. It might already be disabled.");
    } finally {
      setIsLoading(false);
    }
  };


  // Handle enabling a disabled country
  const handleEnableCountry = async (countryId) => {
    setIsLoading(true);

    try {
      await api.delete(`${API_BASE_URL}/countries/disabled/${countryId}`);
      message.success("Country enabled successfully");
      fetchDisabledCountries();
    } catch (error) {
      console.error("Error enabling country:", error);
      message.error("Failed to enable country");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle enabling duty & vat for a country
  const handleEnableDutyVatCountry = async (countryId) => {
    setIsLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/duty-vat/disabled/${countryId}`);
      message.success("Duty & VAT enabled successfully for country");
      fetchDisabledDutyVatCountries();
    } catch (error) {
      console.error("Error enabling duty & VAT for country:", error);
      message.error("Failed to enable duty & VAT for country");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableHsCode = async (hsCodeId) => {
    setIsLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/hs-settings/disabled/${hsCodeId}`);
      message.success("HS Code enabled successfully");
      fetchDisabledHsCodes();
    } catch (error) {
      console.error("Error enabling HS Code:", error);
      message.error("Failed to enable HS Code");
    } finally {
      setIsLoading(false);
    }
  };


  // ECCN Functions
  const handleAddEccn = async (values) => {
    setIsLoading(true);
    try {
      await api.post(`${API_BASE_URL}/eccn`, values);
      message.success("ECCN code added successfully");
      fetchEccnCodes();
      setShowAddEccnModal(false);
      eccnForm.resetFields();
    } catch (error) {
      console.error("Error adding ECCN code:", error);
      message.error("Failed to add ECCN code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditEccn = async (values) => {
    setIsLoading(true);
    try {
      await api.put(`${API_BASE_URL}/eccn/${currentEccn._id}`, values);
      message.success("ECCN code updated successfully");
      fetchEccnCodes();
      setShowEditEccnModal(false);
    } catch (error) {
      console.error("Error updating ECCN code:", error);
      message.error("Failed to update ECCN code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEccn = async () => {
    setIsLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/eccn/${currentEccn._id}`);
      message.success("ECCN code deleted successfully");
      fetchEccnCodes();
      setShowDeleteEccnModal(false);
      setCurrentEccn(null);
    } catch (error) {
      console.error("Error deleting ECCN code:", error);
      message.error("Failed to delete ECCN code");
    } finally {
      setIsLoading(false);
    }
  };

  const showEditModal = (record) => {
    setCurrentEccn(record);
    editEccnForm.setFieldsValue({
      eccn_code: record.eccn_code,
      description: record.description,
      restrictions: record.restrictions
    });
    setShowEditEccnModal(true);
  };

  const showDeleteModal = (record) => {
    setCurrentEccn(record);
    setShowDeleteEccnModal(true);
  };

  // --- NEW: LEAD TIME ACTIONS ---
  const handleLeadTimeEdit = (record) => {
    setCurrentLeadTimeCountry(record);
    leadTimeForm.setFieldsValue({
      fromDays: record.fromDays,
      toDays: record.toDays
    });
    setShowLeadTimeModal(true);
  };

  const handleLeadTimeDelete = async (countryName) => {
    setIsLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/lead-times/${countryName}`);
      message.success("Lead time deleted/reset successfully");
      fetchLeadTimes(); // Refresh table
    } catch (error) {
      console.error("Error deleting lead time:", error);
      message.error("Failed to delete lead time");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadTimeSubmit = async (values) => {
    setIsLoading(true);
    try {
      await api.post(`${API_BASE_URL}/lead-times`, {
        countryName: currentLeadTimeCountry.countryName,
        fromDays: values.fromDays,
        toDays: values.toDays,
        updatedBy: user?.name || user?.email || 'System'
      });
      message.success("Lead time saved successfully");
      setShowLeadTimeModal(false);
      fetchLeadTimes();
    } catch (error) {
      console.error("Error saving lead time:", error);
      message.error("Failed to save lead time");
    } finally {
      setIsLoading(false);
    }
  };

  // Columns for disabled countries table
  const disabledCountriesColumns = [
    {
      title: "Country",
      dataIndex: "countryName",
      key: "countryName",
    },
    {
      title: "Disabled By",
      dataIndex: "disabledBy",
      key: "disabledBy",
      render: (text) => text || "System",
    },
    {
      title: "Disabled Date",
      dataIndex: "disabledDate",
      key: "disabledDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleEnableCountry(record._id)}
          disabled={user?.role !== "super admin"}
        >
          Enable
        </Button>
      ),
    }
  ];

  // Columns for disabled duty & vat table
  const disabledDutyVatColumns = [
    {
      title: "Country",
      dataIndex: "countryName",
      key: "countryName",
    },
    {
      title: "Disabled By",
      dataIndex: "disabledBy",
      key: "disabledBy",
      render: (text) => text || "System",
    },
    {
      title: "Disabled Date",
      dataIndex: "disabledDate",
      key: "disabledDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleEnableDutyVatCountry(record._id)}
          disabled={user?.role !== "super admin"}
        >
          Enable
        </Button>
      ),
    }
  ];

  const disabledHsCodeColumns = [
    {
      title: "HS Code",
      dataIndex: "hsCode",
      key: "hsCode",
    },
    {
      title: "Disabled By",
      dataIndex: "disabledBy",
      key: "disabledBy",
      render: (text) => text || "System",
    },
    {
      title: "Disabled Date",
      dataIndex: "disabledDate",
      key: "disabledDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleEnableHsCode(record._id)}
          disabled={user?.role !== "super admin"}
        >
          Enable
        </Button>
      ),
    }
  ];

  // Columns for ECCN table
  const eccnColumns = [
    {
      title: "ECCN Code",
      dataIndex: "eccn_code",
      key: "eccn_code",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Has Restrictions",
      dataIndex: "restrictions",
      key: "restrictions",
      render: (restrictions) => (
        <span>{restrictions ? "Yes" : "No"}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2">
          <Button
            type="primary"
            onClick={() => showEditModal(record)}
            disabled={user?.role !== "super admin"}
          >
            Edit
          </Button>
          <Button
            type="danger"
            danger
            onClick={() => showDeleteModal(record)}
            disabled={user?.role !== "super admin"}
          >
            Delete
          </Button>
        </div>
      ),
    }
  ];

  // --- NEW: LEAD TIME COLUMNS ---
  const leadTimeColumns = [
    {
      title: "Country Name",
      dataIndex: "countryName",
      key: "countryName",
      sorter: (a, b) => a.countryName.localeCompare(b.countryName),
    },
    {
      title: "From Days",
      dataIndex: "fromDays",
      key: "fromDays",
      render: (text) => text !== null ? text : "-",
    },
    {
      title: "To Days",
      dataIndex: "toDays",
      key: "toDays",
      render: (text) => text !== null ? text : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2">
          <Button
            type="primary"
            onClick={() => handleLeadTimeEdit(record)}
            disabled={user?.role !== "super admin"}
          >
            Edit
          </Button>
          {record.isSaved && (
            <Button
              type="danger"
              danger
              onClick={() => handleLeadTimeDelete(record.countryName)}
              disabled={user?.role !== "super admin"}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    }
  ];

  return (
    <div className="w-full flex flex-col mb-10">
      <div className="mb-0 bg-white py-4 px-3 shadow-md rounded-xl border border-gray-300">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-2">
          <div className="flex flex-wrap items-center space-y-2 flex-shrink-0">
            <div className="flex flex-col items-center w-full md:w-auto gap-y-2 md:flex-row md:space-x-2">
              {activeToggle === "estimation" && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleDropdownToggle}
                    className="w-full md:w-auto flex rounded-[5px] items-center space-x-3 py-2 px-4 text-white btnBg whitespace-nowrap flex-shrink-0"
                  >
                    <FilterIcon />
                    <p className="truncate">
                      {selectedCountries.length > 0
                        ? `${selectedCountries[0]}${selectedCountries.length > 1 ? ` +${selectedCountries.length - 1} more` : ''}`
                        : "Unload Countries"}
                    </p>
                  </button>
                  {dropdown && (
                    <div className="h-[50vh] w-60 overflow-y-auto z-30 absolute left-0 mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-1 space-y-1">

                      <input
                        ref={searchInputRef}
                        className="block w-full px-4 py-2 text-gray-800 rounded-md focus:outline-none"
                        style={{ border: '2px solid #3b82f6' }}
                        type="text"
                        placeholder="Search countries"
                        autoComplete="off"
                        onChange={handleInputChange}
                      />
                      {filteredOptions.length > 0 && (
                        <div className="flex items-center px-4 py-2 cursor-pointer" onClick={handleSelectAll}>
                          <span>{selectAll ? 'Unselect All' : 'Select All'}</span>
                        </div>
                      )}
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((country, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => handleCountrySelect(country)}
                          >
                            <div>{country.value}</div>
                            <div>{selectedCountries.includes(country.value) ? '✓' : ''}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">No results found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Center toggle component */}
          <div className="flex-shrink-0 bg-white py-2 px-3 shadow-md rounded-xl border border-gray-300">
            <div className="flex flex-nowrap gap-1">
              {toggleOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => handleToggleClick(option.key)}
                  className={`${activeToggle === option.key ? "btnBg text-white shadow-lg shadow-[#6478b0]" : ""
                    } text-xs text-black rounded-md text-center py-1.5 px-2 btnHover hover:text-white transition-colors duration-300 whitespace-nowrap flex-shrink-0`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <Tooltip title={user?.role === "super admin" ? "" : "You cannot update IOR in your current role"}>
              <button
                onClick={() => navigate('/update-ior-admin')}
                disabled={user?.role !== "super admin"}
              className={`relative overflow-hidden text-blue-600 border-blue-600 btnBg flex items-center justify-center rounded-[5px] px-3 py-2 whitespace-nowrap ${user?.role !== "super admin" ? "opacity-50 cursor-not-allowed" : "hover:scale-105"}transition-all duration-300`}
              >
                Add IOR Data
              </button>
          </Tooltip>
        </div>
      </div>

      {/* Disabled Country section */}
      {activeToggle === "disableCountry" && (
        <div className="mt-5 bg-white p-6 rounded-md shadow-sm">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Disable Countries</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <MultiCountrySelectInput
                  label="Select Countries to Disable"
                  onChange={handleCountrySelectionForDisable}
                  value={selectedCountriesToDisable}
                  name="disableCountries"
                  id="disableCountries"
                  className="w-full"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="primary"
                  onClick={() => setShowConfirmDisableModal(true)}
                  disabled={selectedCountriesToDisable.length === 0 || user?.role !== "super admin"}
                  className="bg-blue-600"
                >
                  Disable Selected Countries
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Currently Disabled Countries</h3>
            {/* CHANGED: Added dataSource={filteredDisabledCountries} */}
            <Table
              dataSource={filteredDisabledCountries}
              columns={disabledCountriesColumns}
              rowKey="_id"
              loading={isLoading}
              pagination={paginationConfig}
              locale={{ emptyText: "No disabled countries found" }}
            />
          </div>
        </div>
      )}

      {/* ECCN Section */}
      {activeToggle === "eccn" && (
        <div className="mt-5 bg-white p-6 rounded-md shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">ECCN Codes</h3>
            <Button
              type="primary"
              onClick={() => setShowAddEccnModal(true)}
              disabled={user?.role !== "super admin"}
              className="bg-blue-600"
            >
              Add ECCN Code
            </Button>
          </div>
          {/* CHANGED: Added dataSource={filteredEccnData} */}
          <Table
            dataSource={filteredEccnData}
            columns={eccnColumns}
            rowKey="_id"
            loading={isLoading}
            pagination={paginationConfig}
            locale={{ emptyText: "No ECCN codes found" }}
          />
        </div>
      )}

      {/* Disabled Duty & VAT section */}
      {activeToggle === "disableDutyVat" && (
        <div className="mt-5 bg-white p-6 rounded-md shadow-sm">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Disable Duty & VAT for Countries</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <MultiCountrySelectInput
                  label="Select Countries to Disable Duty & VAT"
                  onChange={handleCountrySelectionForDisableDutyVat}
                  value={selectedCountriesToDisableDutyVat}
                  name="disableDutyVatCountries"
                  id="disableDutyVatCountries"
                  className="w-full"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="primary"
                  onClick={() => setShowConfirmDisableDutyVatModal(true)}
                  disabled={selectedCountriesToDisableDutyVat.length === 0 || user?.role !== "super admin"}
                  className="bg-blue-600"
                >
                  Disable Duty & VAT for Selected Countries
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Countries with Duty & VAT Disabled</h3>
            {/* CHANGED: Added dataSource={filteredDutyVatData} */}
            <Table
              dataSource={filteredDutyVatData}
              columns={disabledDutyVatColumns}
              rowKey="_id"
              loading={isLoading}
              pagination={paginationConfig}
              locale={{ emptyText: "No countries found with Duty & VAT disabled" }}
            />
          </div>
        </div>
      )}

      {activeToggle === "disableHsCode" && (
        <div className="mt-5 bg-white p-6 rounded-md shadow-sm">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Disable HS Code</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item
                label="HS Code to Disable"
                required
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter HS Code (e.g., 8517.12)"
                  value={hsCodeToDisable}
                  onChange={handleHsCodeChange}
                  className="w-full"
                // HS Codes can contain numbers, dots, and characters
                />
              </Form.Item>
              <div className="flex items-end">
                <Button
                  type="primary"
                  onClick={() => setShowConfirmDisableHsCodeModal(true)}
                  disabled={hsCodeToDisable.length === 0 || user?.role !== "super admin"}
                  className="bg-blue-600" 
                >
                  Disable HS Code
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Currently Disabled HS Codes</h3>
            {/* CHANGED: Added dataSource={filteredHsCodeData} */}
            <Table
              dataSource={filteredHsCodeData}
              columns={disabledHsCodeColumns}
              rowKey="_id"
              loading={isLoading}
              pagination={paginationConfig}
              locale={{ emptyText: "No disabled HS codes found" }}
            />
          </div>
        </div>
      )}

      {/* --- NEW: LEAD TIMES SECTION --- */}
      {activeToggle === "leadTimes" && (
        <div className="mt-5 bg-white p-6 rounded-md shadow-sm">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Lead Times Configuration</h3>
            <p className="text-gray-500 mb-4">Manage estimated lead times (in days) for each country.</p>
          </div>
          {/* CHANGED: Added dataSource={filteredLeadTimesData} */}
          <Table
            dataSource={filteredLeadTimesData}
            columns={leadTimeColumns}
            rowKey="countryName"
            loading={isLoading}
            pagination={paginationConfig}
            locale={{ emptyText: "No country data available" }}
          />
        </div>
      )}


      {/* Confirmation Modal for Disabling Countries */}
      <Modal
        title="Confirm Disable Countries"
        visible={showConfirmDisableModal}
        onOk={handleDisableCountries}
        onCancel={() => setShowConfirmDisableModal(false)}
      >
        <p>Are you sure you want to disable the selected countries? This will prevent these countries from being used in estimations.</p>
        <div className="mt-4 p-2 bg-gray-50 rounded">
          {selectedCountriesToDisable.map((country, index) => (
            <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2 mb-2">
              {country}
            </span>
          ))}
        </div>
      </Modal>

      {/* Confirmation Modal for Disabling Duty & VAT */}
      <Modal
        title="Confirm Disable Duty & VAT"
        visible={showConfirmDisableDutyVatModal}
        onOk={handleDisableDutyVatCountries}
        onCancel={() => setShowConfirmDisableDutyVatModal(false)}
      >
        <p>Are you sure you want to disable Duty & VAT for the selected countries? This will prevent DHL API calls for duty and tax calculations for these countries.</p>
        <div className="mt-4 p-2 bg-gray-50 rounded">
          {selectedCountriesToDisableDutyVat.map((country, index) => (
            <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2 mb-2">
              {country}
            </span>
          ))}
        </div>
      </Modal>

      <Modal
        title="Confirm Disable HS Code"
        visible={showConfirmDisableHsCodeModal}
        onOk={handleDisableHsCode}
        onCancel={() => setShowConfirmDisableHsCodeModal(false)}
      >
        <p>Are you sure you want to disable the HS Code: <strong className="text-red-500">{hsCodeToDisable}</strong>?</p>
        <p>This will prevent this HS Code from being used in all estimation calculations.</p>
      </Modal>

      {/* Add ECCN Modal */}
      <Modal
        title="Add ECCN Code"
        visible={showAddEccnModal}
        onCancel={() => setShowAddEccnModal(false)}
        footer={null}
      >
        <Form
          form={eccnForm}
          layout="vertical"
          onFinish={handleAddEccn}
        >
          <Form.Item
            name="eccn_code"
            label="ECCN Code"
            rules={[{ required: true, message: "Please enter ECCN code" }]}
          >
            <Input placeholder="Enter ECCN code" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>
          {/* CHANGED: Fixed the Switch logic by removing the wrapping div and using extra prop for description */}
          <Form.Item
            name="restrictions"
            label="Has Restrictions"
            valuePropName="checked"
            initialValue={false}
            extra="Toggle to indicate if this ECCN code has restrictions"
          >
            <Switch className="bg-gray-300" checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item className="text-right">
            <Button type="default" className="mr-2" onClick={() => setShowAddEccnModal(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Add ECCN Code
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit ECCN Modal */}
      <Modal
        title="Edit ECCN Code"
        visible={showEditEccnModal}
        onCancel={() => setShowEditEccnModal(false)}
        footer={null}
      >
        <Form
          form={editEccnForm}
          layout="vertical"
          onFinish={handleEditEccn}
        >
          <Form.Item
            name="eccn_code"
            label="ECCN Code"
            rules={[{ required: true, message: "Please enter ECCN code" }]}
          >
            <Input placeholder="Enter ECCN code" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>
          {/* CHANGED: Fixed the Switch logic for Edit modal as well */}
          <Form.Item
            name="restrictions"
            label="Has Restrictions"
            valuePropName="checked"
            extra="Toggle to indicate if this ECCN code has restrictions"
          >
            <Switch className="bg-gray-300" checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item className="text-right">
            <Button type="default" className="mr-2" onClick={() => setShowEditEccnModal(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Update ECCN Code
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete ECCN Modal */}
      <Modal
        title="Delete ECCN Code"
        visible={showDeleteEccnModal}
        onOk={handleDeleteEccn}
        onCancel={() => setShowDeleteEccnModal(false)}
        okText="Delete"
        okButtonProps={{ danger: true, loading: isLoading }}
      >
        <p>Are you sure you want to delete the ECCN code "{currentEccn?.eccn_code}"?</p>
        <p>This action cannot be undone.</p>
      </Modal>

      {/* --- NEW: LEAD TIME EDIT MODAL --- */}
      <Modal
        title={`Set Lead Time for ${currentLeadTimeCountry?.countryName}`}
        visible={showLeadTimeModal}
        onCancel={() => setShowLeadTimeModal(false)}
        footer={null}
      >
        <Form
          form={leadTimeForm}
          layout="vertical"
          onFinish={handleLeadTimeSubmit}
        >
          <div className="flex space-x-4">
            <Form.Item
              name="fromDays"
              label="From Days"
              className="w-1/2"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={0} className="w-full" placeholder="Min days" />
            </Form.Item>
            <Form.Item
              name="toDays"
              label="To Days"
              className="w-1/2"
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('fromDays') <= value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('To Days must be greater than From Days'));
                  },
                }),
              ]}
            >
              <InputNumber min={0} className="w-full" placeholder="Max days" />
            </Form.Item>
          </div>
          <Form.Item className="text-right">
            <Button type="default" className="mr-2" onClick={() => setShowLeadTimeModal(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UpdateIOR;